// src/pages/ConciliacionExterna/ConciliacionExterna.js
//
// Pantalla de Conciliación Externa (tripartita DGR vs BSJ vs RA).
//
// Diseño: 1 sola pantalla con flujo claro
//   1) Pipeline horizontal (DGR → Pagos BSJ+RA → Auditoría)
//   2) Centro de atención (lo que requiere acción)
//   3) Tabla unificada de auditoría
//   4) Drawers para acciones (importar BSJ, importar RA, conciliar, dashboard)
//
// Protegida por el permiso CONCILIACIÓN BANCARIA en módulo CARGA MASIVA.

import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, Box, Paper, Typography, Tabs, Tab, TextField, Button, Chip, Stack,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    TablePagination, Alert, CircularProgress, MenuItem, IconButton, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel,
    Drawer, Divider,
} from '@mui/material';
import {
    AccountBalance as BancoIcon,
    CloudUpload as UploadIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    PlayArrow as PlayIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    Assessment as AssessmentIcon,
    FileDownload as FileDownloadIcon,
    PictureAsPdf as PictureAsPdfIcon,
    Close as CloseIcon,
    ChevronRight as ChevronRightIcon,
    Inbox as InboxIcon,
    AccountBalanceWallet as PagosIcon,
    FactCheck as AuditoriaIcon,
    InsertChart as DashboardIcon,
    ErrorOutline as ErrorOutlineIcon,
    PendingActions as PendingActionsIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import usePermisosModulo from '../../hooks/usePermisosModulo';
import { parseFechaServer } from '../../utils/fechas';
import DetallePlanDrawer from '../../components/DetallePlanDrawer';
import {
    conciliacionBsjImportarJWT, conciliacionBsjAnularJWT, conciliacionBsjListarJWT, conciliacionBsjDetalleJWT,
    conciliacionRaImportarJWT, conciliacionRaAnularJWT, conciliacionRaListarJWT, conciliacionRaDetalleJWT,
    conciliacionMotorEjecutarJWT, conciliacionAuditoriaSolicitudJWT, conciliacionAuditoriaCertificadoJWT,
    conciliacionMetricasJWT, conciliacionEstadosJWT, conciliacionDashboardJWT,
    conciliacionReprocesoIniciarJWT, conciliacionReprocesoListarJWT, conciliacionReprocesoObtenerJWT,
} from '../../api/endpoints';

const COLOR = '#6a1b9a'; // púrpura Carga Masiva
const BR = 4;

// El AppBar global de SIGIF mide 64px desktop / 56px mobile. Los drawers se
// renderizan en un Portal raíz, así que su Paper aparece por DEBAJO del AppBar
// si no le seteamos `top`. Sin este fix el header del drawer queda escondido
// detrás del header azul de la app y nadie sabe en qué pantalla está.
const DRAWER_PAPER_SX = {
    width: { xs: '100%', md: '85%', lg: '75%' },
    top: { xs: '56px', sm: '64px' },
    height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' },
};

const ESTADO_COLOR = { V: 'success', X: 'error', S: 'warning', D: 'default', W: 'info' };
const ESTADO_LABEL = { V: 'Conciliado', X: 'Sin Coincidencia', S: 'Sin Pagos', W: 'Sin Solicitud DGR', D: 'Duplicado' };
const ESTADO_HEX = { V: '#2e7d32', X: '#c62828', S: '#e65100', W: '#0277bd', D: '#757575' };

function fmt(n) { return Number(n || 0).toLocaleString('es-AR', { maximumFractionDigits: 0 }); }
function fmtMonto(n) { return Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtFecha(d) {
    if (!d) return '';
    const s = String(d);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    const x = parseFechaServer(d);
    return x ? x.toLocaleDateString('es-AR') : '';
}
function fmtDateTime(d) {
    if (!d) return '';
    try { return new Date(d).toLocaleString('es-AR'); } catch { return String(d); }
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================
export default function ConciliacionExterna() {
    const { enqueueSnackbar } = useSnackbar();
    const permisos = usePermisosModulo('CARGA MASIVA');
    const puedeVer = permisos.has('CONCILIACIÓN BANCARIA', 'SELECT');
    const puedeCargar = permisos.has('CONCILIACIÓN BANCARIA', 'INSERT');
    const puedeConciliar = permisos.has('CONCILIACIÓN BANCARIA', 'UPDATE');
    const puedeEliminar = permisos.has('CONCILIACIÓN BANCARIA', 'DELETE');

    const [metricas, setMetricas] = useState(null);
    const [contadoresPagos, setContadoresPagos] = useState({ bsjArchivos: 0, raArchivos: 0, huerfanosBsj: 0, huerfanosRa: 0 });

    // Drawers — puede ser un string o un objeto { tipo, modo } para indicar
    // sub-tab inicial (importar vs consultar) cuando se abre desde un boton
    // especifico del pipeline.
    const [drawer, setDrawer] = useState(null); // 'bsj' | 'ra' | 'conciliar' | 'dashboard' | { tipo, modo }
    // Filtros pre-aplicados a la tabla central (cuando se clickea una pipeline card o atención)
    const [filtroInicial, setFiltroInicial] = useState(null);
    // Triggers separados: tabla y métricas se recargan independientemente.
    const [reloadTabla, setReloadTabla] = useState(0);
    const [reloadMetricas, setReloadMetricas] = useState(0);
    const [cargandoMetricas, setCargandoMetricas] = useState(false);

    const cargarMetricas = useCallback(async () => {
        setCargandoMetricas(true);
        try {
            const r = await conciliacionMetricasJWT();
            if (r?.ok) setMetricas(r.metricas);
        } catch { /* silencioso */ }
        // Contadores de "pagos" (archivos BSJ + RA cargados + huérfanos vivos)
        try {
            const [rBsj, rRa] = await Promise.all([
                conciliacionBsjListarJWT({ page: 1, pageSize: 1 }),
                conciliacionRaListarJWT({ page: 1, pageSize: 1 }),
            ]);
            setContadoresPagos(c => ({
                ...c,
                bsjArchivos: rBsj?.ok ? Number(rBsj.total || 0) : 0,
                raArchivos: rRa?.ok ? Number(rRa.total || 0) : 0,
            }));
        } catch { /* silencioso */ }
        setCargandoMetricas(false);
    }, []);

    useEffect(() => { if (puedeVer) cargarMetricas(); }, [puedeVer, cargarMetricas, reloadMetricas]);

    const refrescarTodo = () => { setReloadMetricas(k => k + 1); setReloadTabla(k => k + 1); };
    const refrescarTabla = () => { setReloadTabla(k => k + 1); };

    if (!permisos.ready) {
        return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
    }
    if (!puedeVer) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error">
                    No tenés permiso para ver esta pantalla. Pedile a un director que te habilite la función
                    <strong> CONCILIACIÓN BANCARIA</strong> en el módulo <strong>CARGA MASIVA</strong>.
                </Alert>
            </Box>
        );
    }

    const m = metricas || {};
    const aRevisar = (m.SinCoincidencia || 0) + (m.SinSolicitud || 0) + (m.Duplicados || 0);
    const totalConciliacion = (m.Total || 0);
    const pctConciliados = totalConciliacion > 0 ? Math.round((m.Conciliados || 0) / totalConciliacion * 100) : 0;

    const items = [];
    if (aRevisar > 0) {
        items.push({
            key: 'revisar', icon: <ErrorOutlineIcon sx={{ color: '#c62828' }} />, color: '#c62828',
            label: `${aRevisar.toLocaleString('es-AR')} registros con problema de conciliación`,
            sub: `${m.SinCoincidencia || 0} sin coincidencia · ${m.SinSolicitud || 0} sin solicitud · ${m.Duplicados || 0} duplicados`,
            onClick: () => { setFiltroInicial({ estado: 'X' }); refrescarTabla(); },
        });
    }
    if ((m.SinDatos || 0) > 0) {
        items.push({
            key: 'sinPagos', icon: <PendingActionsIcon sx={{ color: '#e65100' }} />, color: '#e65100',
            label: `${(m.SinDatos || 0).toLocaleString('es-AR')} planes DGR sin pago aún`,
            sub: 'Planes DGR sembrados que esperan archivo BSJ o RA para conciliarse',
            onClick: () => { setFiltroInicial({ estado: 'S' }); refrescarTabla(); },
        });
    }

    return (
        <Box sx={{ bgcolor: '#f6f8fc', minHeight: '100vh', py: 2, px: { xs: 2, md: 3 } }}>
            <Container maxWidth={false} disableGutters>
                {/* ── Header ────────────────────────────────────────────────── */}
                <Stack direction="row" alignItems="center" sx={{ mb: 2, gap: 1.5, flexWrap: 'wrap' }}>
                    <BancoIcon sx={{ color: COLOR, fontSize: 36 }} />
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: COLOR }}>
                            Conciliación Externa
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            DGR — Banco San Juan — Registro Automotor
                        </Typography>
                    </Box>
                    <Tooltip title="Refrescar todo">
                        <IconButton onClick={refrescarTodo} disabled={cargandoMetricas}>
                            {cargandoMetricas ? <CircularProgress size={20} /> : <RefreshIcon />}
                        </IconButton>
                    </Tooltip>
                    <Button variant="outlined" startIcon={<DashboardIcon />}
                        onClick={() => setDrawer('dashboard')}
                        sx={{ color: COLOR, borderColor: COLOR, textTransform: 'none', fontWeight: 600 }}>
                        Dashboard
                    </Button>
                    <Button variant="outlined" startIcon={<UploadIcon />}
                        onClick={() => setDrawer({ tipo: 'bsj', modo: 'importar', plusPagos: false })} disabled={!puedeCargar}
                        sx={{ color: COLOR, borderColor: COLOR, textTransform: 'none', fontWeight: 600 }}>
                        Importar BSJ
                    </Button>
                    <Button variant="outlined" startIcon={<UploadIcon />}
                        onClick={() => setDrawer({ tipo: 'bsj', modo: 'importar', plusPagos: true })} disabled={!puedeCargar}
                        sx={{ color: COLOR, borderColor: COLOR, textTransform: 'none', fontWeight: 600 }}>
                        Importar Botón Pago
                    </Button>
                    <Button variant="outlined" startIcon={<UploadIcon />}
                        onClick={() => setDrawer({ tipo: 'ra', modo: 'importar' })} disabled={!puedeCargar}
                        sx={{ color: COLOR, borderColor: COLOR, textTransform: 'none', fontWeight: 600 }}>
                        Registro Automotor
                    </Button>
                    <Button variant="contained" startIcon={<PlayIcon />}
                        onClick={() => setDrawer('conciliar')} disabled={!puedeConciliar}
                        sx={{ bgcolor: COLOR, '&:hover': { bgcolor: '#4a1370' }, textTransform: 'none', fontWeight: 600 }}>
                        Conciliar
                    </Button>
                </Stack>

                {/* ── Pipeline ────────────────────────────────────────────────── */}
                <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 32px 1fr 32px 1fr' }, alignItems: 'stretch', mb: 2 }}>
                    <PipelineCard
                        num="1" icon={<InboxIcon />} titulo="DGR (PLANES)" color={COLOR}
                        valorPrincipal={(m.Total || 0).toLocaleString('es-AR')}
                        valorLabel="planes en seguimiento"
                        lineas={[
                            { label: 'Planes sin pago (S)', valor: (m.SinDatos || 0).toLocaleString('es-AR') },
                            { label: 'Cron diario', valor: 'Activo' },
                        ]}
                        onClick={() => { setFiltroInicial({ origen: 'DGR' }); refrescarTabla(); }}
                    />
                    <FlechaPipeline />
                    <PipelineCard
                        num="2" icon={<PagosIcon />} titulo="PAGOS (BSJ + RA)" color="#1565c0"
                        valorPrincipal={fmt(contadoresPagos.bsjArchivos + contadoresPagos.raArchivos)}
                        valorLabel="archivos cargados"
                        lineas={[
                            { label: 'Archivos BSJ / BP', valor: fmt(contadoresPagos.bsjArchivos) },
                            { label: 'Archivos RA', valor: fmt(contadoresPagos.raArchivos) },
                        ]}
                        accion={puedeCargar ? { label: 'Importar BSJ', onClick: () => setDrawer({ tipo: 'bsj', modo: 'importar' }) } : null}
                        onClick={() => setDrawer('bsj')}
                    />
                    <FlechaPipeline />
                    <PipelineCard
                        num="3" icon={<AuditoriaIcon />} titulo="ESTADO DEL CRUCE" color={aRevisar > 0 ? '#f57c00' : '#2e7d32'}
                        valorPrincipal={`${pctConciliados}%`}
                        valorLabel="conciliados"
                        lineas={[
                            { label: 'Conciliados (V)', valor: (m.Conciliados || 0).toLocaleString('es-AR') },
                            { label: 'Sin coincidencia (X)', valor: (m.SinCoincidencia || 0).toLocaleString('es-AR'), warn: (m.SinCoincidencia || 0) > 0 },
                        ]}
                        onClick={() => { setFiltroInicial({ estado: 'V' }); refrescarTabla(); }}
                    />
                </Box>

                {/* ── Centro de atención ────────────────────────────────────── */}
                {items.length > 0 && (
                    <Paper sx={{ p: 1.5, mb: 2, borderRadius: `${BR}px`, borderLeft: '4px solid #f57c00' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: '#666' }}>
                            ⚠ Requieren tu atención ({items.length})
                        </Typography>
                        <Stack divider={<Divider />} spacing={0}>
                            {items.map(it => (
                                <AtencionItem key={it.key} icon={it.icon} color={it.color}
                                    label={it.label} sub={it.sub} onClick={it.onClick} />
                            ))}
                        </Stack>
                    </Paper>
                )}

                {/* ── Tabla unificada (Auditoría) ───────────────────────────── */}
                <Paper sx={{ borderRadius: `${BR}px`, p: 0 }}>
                    <TablaAuditoriaUnificada
                        filtroInicial={filtroInicial}
                        enqueueSnackbar={enqueueSnackbar}
                        reloadKey={reloadTabla}
                    />
                </Paper>
            </Container>

            {/* ── Drawers ──────────────────────────────────────────────────── */}
            <Drawer anchor="right" open={drawer === 'bsj' || drawer?.tipo === 'bsj'} onClose={() => setDrawer(null)}
                PaperProps={{ sx: DRAWER_PAPER_SX }}>
                {(drawer === 'bsj' || drawer?.tipo === 'bsj') && (
                    <DrawerImportarBSJ
                        puedeCargar={puedeCargar} puedeEliminar={puedeEliminar}
                        enqueueSnackbar={enqueueSnackbar}
                        modoInicial={drawer?.modo || 'consultar'}
                        plusPagosInicial={drawer?.plusPagos === true}
                        onClose={() => setDrawer(null)} onCambio={refrescarTodo}
                    />
                )}
            </Drawer>

            <Drawer anchor="right" open={drawer === 'ra' || drawer?.tipo === 'ra'} onClose={() => setDrawer(null)}
                PaperProps={{ sx: DRAWER_PAPER_SX }}>
                {(drawer === 'ra' || drawer?.tipo === 'ra') && (
                    <DrawerImportarRA
                        puedeCargar={puedeCargar} puedeEliminar={puedeEliminar}
                        enqueueSnackbar={enqueueSnackbar}
                        modoInicial={drawer?.modo || 'consultar'}
                        onClose={() => setDrawer(null)} onCambio={refrescarTodo}
                    />
                )}
            </Drawer>

            <Drawer anchor="right" open={drawer === 'conciliar'} onClose={() => setDrawer(null)}
                PaperProps={{ sx: DRAWER_PAPER_SX }}>
                {drawer === 'conciliar' && (
                    <DrawerConciliar
                        puedeConciliar={puedeConciliar}
                        enqueueSnackbar={enqueueSnackbar}
                        onClose={() => setDrawer(null)} onCambio={refrescarTodo}
                    />
                )}
            </Drawer>

            <Drawer anchor="right" open={drawer === 'dashboard'} onClose={() => setDrawer(null)}
                PaperProps={{ sx: DRAWER_PAPER_SX }}>
                {drawer === 'dashboard' && (
                    <DrawerDashboard onClose={() => setDrawer(null)} />
                )}
            </Drawer>
        </Box>
    );
}

// ============================================================================
// Pipeline + Atención (componentes visuales)
// ============================================================================
function PipelineCard({ num, icon, titulo, color, valorPrincipal, valorLabel, lineas = [], accion, onClick }) {
    return (
        <Paper variant="outlined" onClick={onClick}
            sx={{
                p: 2, borderRadius: `${BR}px`, cursor: onClick ? 'pointer' : 'default',
                borderTop: `3px solid ${color}`, transition: 'all 0.15s',
                '&:hover': onClick ? { boxShadow: 2, transform: 'translateY(-2px)' } : {},
                display: 'flex', flexDirection: 'column', gap: 1,
            }}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ bgcolor: color, color: 'white', width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>
                    {num}
                </Box>
                <Box sx={{ color, display: 'flex' }}>{icon}</Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', letterSpacing: 0.5 }}>
                    {titulo}
                </Typography>
            </Stack>
            <Box sx={{ pl: 0.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color, lineHeight: 1.1 }}>
                    {valorPrincipal}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {valorLabel}
                </Typography>
            </Box>
            {lineas.length > 0 && (
                <Stack spacing={0.25} sx={{ pt: 0.5, borderTop: '1px dashed #eee' }}>
                    {lineas.map((l, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                            <Typography variant="caption" color="text.secondary">{l.label}</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: l.warn ? '#f57c00' : 'inherit' }}>
                                {l.valor}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            )}
            {accion && (
                <Button size="small" variant="outlined"
                    onClick={(e) => { e.stopPropagation(); accion.onClick(); }}
                    sx={{ alignSelf: 'flex-start', textTransform: 'none', mt: 0.5, color, borderColor: color }}>
                    {accion.label}
                </Button>
            )}
        </Paper>
    );
}

function FlechaPipeline() {
    return (
        <Box sx={{
            display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'center',
            color: '#bbb',
        }}>
            <ChevronRightIcon sx={{ fontSize: 32 }} />
        </Box>
    );
}

function AtencionItem({ icon, color, label, sub, onClick }) {
    return (
        <Box onClick={onClick} sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 0.5,
            cursor: 'pointer', borderRadius: 1,
            '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' },
        }}>
            <Box sx={{ display: 'flex' }}>{icon}</Box>
            <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color }}>
                    {label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {sub}
                </Typography>
            </Box>
            <ChevronRightIcon sx={{ color: '#999' }} />
        </Box>
    );
}

// ============================================================================
// TablaAuditoriaUnificada — el centro de la pantalla (antes TabAuditoria)
// ============================================================================
function TablaAuditoriaUnificada({ filtroInicial, enqueueSnackbar, reloadKey }) {
    const [subtab, setSubtab] = useState('solicitud');
    const [filtros, setFiltros] = useState({
        fechaDesde: '', fechaHasta: '',
        estado: filtroInicial?.estado || '',
        origen: filtroInicial?.origen || '',
        solicitud: '', certificado: '',
    });
    const [metricas, setMetricas] = useState(null);
    const [estados, setEstados] = useState([]);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [loading, setLoading] = useState(false);
    const [exportando, setExportando] = useState(false);

    useEffect(() => {
        conciliacionEstadosJWT().then(r => { if (r?.ok) setEstados(r.estados); }).catch(() => {});
    }, []);

    // Cuando viene un filtro inicial nuevo (click en pipeline o atención),
    // lo aplicamos y volvemos a la página 0.
    useEffect(() => {
        if (filtroInicial) {
            setFiltros(f => ({
                ...f,
                estado: filtroInicial.estado !== undefined ? filtroInicial.estado : f.estado,
                origen: filtroInicial.origen !== undefined ? filtroInicial.origen : f.origen,
            }));
            setPage(0);
        }
    }, [filtroInicial]);

    const cargarMetricas = useCallback(() => {
        // Pasamos filtros menos estado (los chips son el control de estado).
        conciliacionMetricasJWT(filtros.fechaDesde, filtros.fechaHasta, filtros.origen, filtros.solicitud)
            .then(r => { if (r?.ok) setMetricas(r.metricas); }).catch(() => {});
    }, [filtros.fechaDesde, filtros.fechaHasta, filtros.origen, filtros.solicitud]);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const req = subtab === 'solicitud' ? conciliacionAuditoriaSolicitudJWT : conciliacionAuditoriaCertificadoJWT;
            const r = await req({ ...filtros, page: page + 1, pageSize });
            if (r?.ok) { setRows(r.items || []); setTotal(r.total || 0); }
            else { setRows([]); setTotal(0); }
        } finally { setLoading(false); }
    }, [subtab, filtros, page, pageSize]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { cargar(); }, [subtab, filtros, page, pageSize, reloadKey]);
    useEffect(() => { cargarMetricas(); }, [cargarMetricas, reloadKey]);

    // ── Export ──────────────────────────────────────────────────────────────
    const cargarTodoParaExport = async () => {
        const req = subtab === 'solicitud' ? conciliacionAuditoriaSolicitudJWT : conciliacionAuditoriaCertificadoJWT;
        const r = await req({ ...filtros, page: 1, pageSize: 10000 });
        return r?.ok ? (r.items || []) : [];
    };

    const tituloReporte = () => {
        const partes = ['Estado del cruce DGR/BSJ/RA'];
        partes.push(subtab === 'solicitud' ? 'Por Solicitud' : 'Por Certificado');
        if (filtros.fechaDesde) partes.push(`Desde: ${fmtFecha(filtros.fechaDesde)}`);
        if (filtros.fechaHasta) partes.push(`Hasta: ${fmtFecha(filtros.fechaHasta)}`);
        if (filtros.estado) partes.push(`Estado: ${ESTADO_LABEL[filtros.estado] || filtros.estado}`);
        if (filtros.origen) partes.push(`Origen: ${filtros.origen}`);
        if (filtros.solicitud) partes.push(`Solicitud: ${filtros.solicitud}`);
        if (filtros.certificado) partes.push(`Certificado: ${filtros.certificado}`);
        return partes.join(' — ');
    };

    const handleExportExcel = async () => {
        setExportando(true);
        try {
            const items = await cargarTodoParaExport();
            const wb = XLSX.utils.book_new();
            const head = subtab === 'solicitud'
                ? [['Solicitud', 'Certificados', 'DGR Hon.', 'DGR Gas.', 'BSJ Hon.', 'BSJ Gas.', 'RA Hon.', 'RA Gas.', 'Fecha', 'Origen', 'Estado']]
                : [['Solicitud', 'Serie', 'Número', 'Cuota', 'DGR Hon.', 'DGR Gas.', 'BSJ Hon.', 'BSJ Gas.', 'RA Hon.', 'RA Gas.', 'Fecha', 'Origen', 'Estado']];
            const body = items.map(r => subtab === 'solicitud'
                ? [r.Solicitud, r.Certificados, Number(r.DGR_Honorarios || 0), Number(r.DGR_Gastos || 0),
                   Number(r.BSJ_Honorarios || 0), Number(r.BSJ_Gastos || 0), Number(r.RA_Honorarios || 0), Number(r.RA_Gastos || 0),
                   fmtFecha(r.Fecha_Deposito), (r.Origen || '').trim(),
                   ESTADO_LABEL[r.Estado_Code] || r.Estado_Descripcion || r.Estado_Code]
                : [r.Solicitud, (r.Serie || '').trim(), r.Numero, r.Cuota,
                   Number(r.DGR_Honorarios || 0), Number(r.DGR_Gastos || 0),
                   Number(r.BSJ_Honorarios || 0), Number(r.BSJ_Gastos || 0),
                   Number(r.RA_Honorarios || 0), Number(r.RA_Gastos || 0),
                   fmtFecha(r.Fecha_Deposito), (r.Origen || '').trim(),
                   ESTADO_LABEL[r.Estado_Code] || r.Estado_Descripcion || r.Estado_Code]);
            const ws = XLSX.utils.aoa_to_sheet([...head, ...body]);
            ws['!cols'] = head[0].map(() => ({ wch: 14 }));
            XLSX.utils.book_append_sheet(wb, ws, 'Cruce DGR-BSJ-RA');
            const fecha = new Date().toISOString().slice(0, 10);
            XLSX.writeFile(wb, `Cruce_${subtab}_${fecha}.xlsx`);
            enqueueSnackbar(`${items.length} registros exportados`, { variant: 'success' });
        } catch {
            enqueueSnackbar('Error al exportar a Excel', { variant: 'error' });
        } finally { setExportando(false); }
    };

    const handleExportPDF = async () => {
        setExportando(true);
        try {
            const items = await cargarTodoParaExport();
            const doc = new jsPDF({ orientation: 'landscape' });
            const margin = 14;
            doc.setFontSize(14);
            doc.setTextColor(106, 27, 154);
            doc.text(tituloReporte(), margin, 15);
            doc.setFontSize(9);
            doc.setTextColor(120);
            doc.text(`${items.length} registro(s) · Generado: ${new Date().toLocaleString('es-AR')}`, margin, 21);

            const head = subtab === 'solicitud'
                ? [['Solicitud', 'Cert.', 'DGR H.', 'DGR G.', 'BSJ H.', 'BSJ G.', 'RA H.', 'RA G.', 'Fecha', 'Origen', 'Estado']]
                : [['Sol.', 'Ser.', 'Núm.', 'Cuota', 'DGR H.', 'DGR G.', 'BSJ H.', 'BSJ G.', 'RA H.', 'RA G.', 'Fecha', 'Origen', 'Estado']];
            const body = items.map(r => subtab === 'solicitud'
                ? [r.Solicitud, r.Certificados, fmtMonto(r.DGR_Honorarios), fmtMonto(r.DGR_Gastos),
                   fmtMonto(r.BSJ_Honorarios), fmtMonto(r.BSJ_Gastos), fmtMonto(r.RA_Honorarios), fmtMonto(r.RA_Gastos),
                   fmtFecha(r.Fecha_Deposito), (r.Origen || '').trim(),
                   ESTADO_LABEL[r.Estado_Code] || r.Estado_Descripcion || r.Estado_Code]
                : [r.Solicitud, (r.Serie || '').trim(), r.Numero, r.Cuota,
                   fmtMonto(r.DGR_Honorarios), fmtMonto(r.DGR_Gastos),
                   fmtMonto(r.BSJ_Honorarios), fmtMonto(r.BSJ_Gastos),
                   fmtMonto(r.RA_Honorarios), fmtMonto(r.RA_Gastos),
                   fmtFecha(r.Fecha_Deposito), (r.Origen || '').trim(),
                   ESTADO_LABEL[r.Estado_Code] || r.Estado_Descripcion || r.Estado_Code]);

            autoTable(doc, {
                startY: 26,
                head, body,
                theme: 'striped',
                headStyles: { fillColor: [106, 27, 154], textColor: 255 },
                styles: { fontSize: 7, cellPadding: 1.2 },
                margin: { left: margin, right: margin },
            });

            const fecha = new Date().toISOString().slice(0, 10);
            const filename = `Cruce_${subtab}_${fecha}.pdf`;
            const blob = doc.output('blob');
            const url = URL.createObjectURL(blob);
            const win = window.open(url, '_blank');
            if (!win) doc.save(filename);
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
            enqueueSnackbar(`${items.length} registros exportados a PDF`, { variant: 'success' });
        } catch {
            enqueueSnackbar('Error al exportar a PDF', { variant: 'error' });
        } finally { setExportando(false); }
    };

    return (
        <Box>
            {/* Barra superior */}
            <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', borderBottom: '1px solid #eee' }}>
                <Tabs value={subtab} onChange={(_, v) => { setSubtab(v); setPage(0); }}>
                    <Tab value="solicitud" label="Por Solicitud" />
                    <Tab value="certificado" label="Por Certificado" />
                </Tabs>
                <Box sx={{ flexGrow: 1 }} />
                <Chip size="small" label={`${fmt(total)} registros`} color="default" />
            </Box>

            {/* Chips de estado (clickeables) */}
            {metricas && (
                <Box sx={{ px: 1.5, pt: 1.5 }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip icon={<AssessmentIcon />} label={`Total: ${fmt(metricas.Total)}`}
                            color={filtros.estado === '' ? 'primary' : 'default'}
                            variant={filtros.estado === '' ? 'filled' : 'outlined'}
                            onClick={() => { setFiltros(f => ({ ...f, estado: '' })); setPage(0); }} />
                        <Chip label={`Conciliados: ${fmt(metricas.Conciliados)}`} color="success" size="small"
                            variant={filtros.estado === 'V' ? 'filled' : 'outlined'}
                            onClick={() => { setFiltros(f => ({ ...f, estado: 'V' })); setPage(0); }} />
                        <Chip label={`Sin Coincidencia: ${fmt(metricas.SinCoincidencia)}`} color="error" size="small"
                            variant={filtros.estado === 'X' ? 'filled' : 'outlined'}
                            onClick={() => { setFiltros(f => ({ ...f, estado: 'X' })); setPage(0); }} />
                        <Chip label={`Sin Pagos: ${fmt(metricas.SinDatos)}`} color="warning" size="small"
                            variant={filtros.estado === 'S' ? 'filled' : 'outlined'}
                            onClick={() => { setFiltros(f => ({ ...f, estado: 'S' })); setPage(0); }} />
                        <Chip label={`Sin Solicitud: ${fmt(metricas.SinSolicitud)}`} color="info" size="small"
                            variant={filtros.estado === 'W' ? 'filled' : 'outlined'}
                            onClick={() => { setFiltros(f => ({ ...f, estado: 'W' })); setPage(0); }} />
                        <Chip label={`Duplicados: ${fmt(metricas.Duplicados)}`} size="small"
                            color={filtros.estado === 'D' ? 'primary' : 'default'}
                            variant={filtros.estado === 'D' ? 'filled' : 'outlined'}
                            onClick={() => { setFiltros(f => ({ ...f, estado: 'D' })); setPage(0); }} />
                    </Stack>
                </Box>
            )}

            {/* Filtros + acciones */}
            <Box sx={{ p: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', bgcolor: '#fafbfc' }}>
                <TextField type="date" size="small" label="Desde" InputLabelProps={{ shrink: true }}
                    inputProps={{ min: '2020-01-01', max: '2099-12-31' }}
                    value={filtros.fechaDesde} onChange={e => setFiltros(f => ({ ...f, fechaDesde: e.target.value }))}
                    sx={{ minWidth: 150 }} />
                <TextField type="date" size="small" label="Hasta" InputLabelProps={{ shrink: true }}
                    inputProps={{ min: '2020-01-01', max: '2099-12-31' }}
                    value={filtros.fechaHasta} onChange={e => setFiltros(f => ({ ...f, fechaHasta: e.target.value }))}
                    sx={{ minWidth: 150 }} />
                <TextField select size="small" label="Estado" sx={{ minWidth: 160 }}
                    value={filtros.estado} onChange={e => setFiltros(f => ({ ...f, estado: e.target.value }))}>
                    <MenuItem value="">Todos</MenuItem>
                    {estados.map(e => <MenuItem key={e.Id} value={e.Id}>{e.Estado}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Origen" sx={{ minWidth: 140 }}
                    value={filtros.origen} onChange={e => setFiltros(f => ({ ...f, origen: e.target.value }))}>
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="DGR">DGR</MenuItem>
                    <MenuItem value="BSJ">BSJ</MenuItem>
                    <MenuItem value="BP">BSJ (Botón Pago)</MenuItem>
                    <MenuItem value="RA">RA</MenuItem>
                </TextField>
                <TextField size="small" label="Solicitud" value={filtros.solicitud}
                    onChange={e => setFiltros(f => ({ ...f, solicitud: e.target.value }))} sx={{ width: 140 }} />
                {subtab === 'certificado' && (
                    <TextField size="small" label="Certificado" value={filtros.certificado}
                        onChange={e => setFiltros(f => ({ ...f, certificado: e.target.value }))} sx={{ width: 140 }} />
                )}
                <Button variant="contained" size="small" startIcon={<SearchIcon />}
                    sx={{ bgcolor: COLOR }} onClick={() => { setPage(0); cargar(); cargarMetricas(); }}>Buscar</Button>
                <IconButton size="small" onClick={() => cargar()}><RefreshIcon /></IconButton>
                <Box sx={{ flexGrow: 1 }} />
                <Tooltip title="Exportar a Excel (todos los filtrados, hasta 10.000)">
                    <span>
                        <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />}
                            onClick={handleExportExcel} disabled={exportando || total === 0}
                            sx={{ textTransform: 'none' }}>
                            Excel
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title="Exportar a PDF (preview en nueva pestaña)">
                    <span>
                        <Button size="small" variant="outlined" color="error" startIcon={<PictureAsPdfIcon />}
                            onClick={handleExportPDF} disabled={exportando || total === 0}
                            sx={{ textTransform: 'none' }}>
                            PDF
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : subtab === 'solicitud' ? (
                <TablaAuditoriaSolicitud rows={rows} />
            ) : (
                <TablaAuditoriaCertificado rows={rows} />
            )}

            <TablePagination component="div" count={total} page={page} rowsPerPage={pageSize}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50, 100]} />
        </Box>
    );
}

function TablaAuditoriaSolicitud({ rows }) {
    const [certsDialog, setCertsDialog] = useState(null);
    return (
        <>
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0, borderLeft: 0, borderRight: 0 }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ bgcolor: '#fafafa' }}>Solicitud</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }} align="center">Certificados</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }} align="right">DGR Hon.</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }} align="right">DGR Gas.</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }} align="right">BSJ Hon.</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }} align="right">BSJ Gas.</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }} align="right">RA Hon.</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }} align="right">RA Gas.</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }}>Fecha</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }}>Origen</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }}>Estado</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.length === 0 ? (
                        <TableRow><TableCell colSpan={11} align="center" sx={{ py: 3, color: 'text.secondary' }}>Sin resultados</TableCell></TableRow>
                    ) : rows.map((r, i) => (
                        <TableRow key={`${r.Solicitud}-${r.Fecha_Deposito}-${i}`} hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => setCertsDialog({ solicitud: r.Solicitud, fecha: r.Fecha_Deposito })}>
                            <TableCell>{r.Solicitud}</TableCell>
                            <TableCell align="center">{r.Certificados}</TableCell>
                            <TableCell align="right">{fmtMonto(r.DGR_Honorarios)}</TableCell>
                            <TableCell align="right">{fmtMonto(r.DGR_Gastos)}</TableCell>
                            <TableCell align="right">{fmtMonto(r.BSJ_Honorarios)}</TableCell>
                            <TableCell align="right">{fmtMonto(r.BSJ_Gastos)}</TableCell>
                            <TableCell align="right">{fmtMonto(r.RA_Honorarios)}</TableCell>
                            <TableCell align="right">{fmtMonto(r.RA_Gastos)}</TableCell>
                            <TableCell>{fmtFecha(r.Fecha_Deposito)}</TableCell>
                            <TableCell>{(r.Origen || '').trim()}</TableCell>
                            <TableCell><Chip size="small" label={ESTADO_LABEL[r.Estado_Code] || r.Estado_Descripcion || r.Estado_Code} color={ESTADO_COLOR[r.Estado_Code] || 'default'} /></TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
        <CertificadosDeAuditoriaDialog
            open={!!certsDialog} onClose={() => setCertsDialog(null)}
            solicitud={certsDialog?.solicitud} fecha={certsDialog?.fecha} />
        </>
    );
}

function CertificadosDeAuditoriaDialog({ open, onClose, solicitud, fecha }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [planDetalleId, setPlanDetalleId] = useState(null);
    useEffect(() => {
        if (!open || !solicitud) return;
        setLoading(true);
        const fechaIso = fecha ? String(fecha).slice(0, 10) : undefined;
        conciliacionAuditoriaCertificadoJWT({
            solicitud, fechaDesde: fechaIso, fechaHasta: fechaIso, page: 1, pageSize: 100,
        })
            .then(r => { if (r?.ok) setRows(r.items || []); else setRows([]); })
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
    }, [open, solicitud, fecha]);

    return (
        <Dialog open={!!open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ bgcolor: COLOR, color: 'white', fontWeight: 700 }}>
                Certificados — Solicitud {solicitud} · {fmtFecha(fecha)}
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
                {loading ? (
                    <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
                ) : rows.length === 0 ? (
                    <Alert severity="info" sx={{ m: 2 }}>No hay certificados para esta solicitud y fecha.</Alert>
                ) : (() => {
                    const hayReplicados = rows.some(r => Number(r.BSJ_Replicado_En || 0) > 1);
                    return (
                    <>
                    {hayReplicados && (
                        <Alert severity="info" icon={false} sx={{ m: 1.5, py: 0.5, fontSize: '0.78rem', borderRadius: `${BR}px`, bgcolor: '#e3f2fd' }}>
                            <strong>*</strong> El monto BSJ marcado con asterisco está <strong>repetido en cada certificado</strong> de la misma solicitud/cuota — es <strong>1 solo pago bancario</strong>, no uno por certificado. No sumar entre filas.
                        </Alert>
                    )}
                    <TableContainer>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow sx={{ '& th': { bgcolor: '#fafafa', fontWeight: 700 } }}>
                                    <TableCell>Solicitud</TableCell>
                                    <TableCell>Serie</TableCell>
                                    <TableCell>Número</TableCell>
                                    <TableCell align="center">Cuota</TableCell>
                                    <TableCell align="right">DGR Hon.</TableCell>
                                    <TableCell align="right">DGR Gas.</TableCell>
                                    <TableCell align="right">BSJ Hon.</TableCell>
                                    <TableCell align="right">BSJ Gas.</TableCell>
                                    <TableCell align="right">RA Hon.</TableCell>
                                    <TableCell align="right">RA Gas.</TableCell>
                                    <TableCell>Origen</TableCell>
                                    <TableCell>Estado</TableCell>
                                    <TableCell align="center">Detalle</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((r, i) => {
                                    const replicado = Number(r.BSJ_Replicado_En || 0) > 1;
                                    const tooltipBsj = replicado
                                        ? `Este pago BSJ es de toda la solicitud ($${fmtMonto(r.BSJ_Honorarios)}). Aparece replicado en los ${r.BSJ_Replicado_En} certificados de la misma cuota — NO sumar entre filas.`
                                        : '';
                                    const sxBsj = replicado ? { color: '#1565c0', fontStyle: 'italic' } : {};
                                    return (
                                    <TableRow key={`${r.Solicitud}-${r.Serie}-${r.Numero}-${i}`} hover>
                                        <TableCell>{r.Solicitud}</TableCell>
                                        <TableCell>{(r.Serie || '').trim()}</TableCell>
                                        <TableCell>{r.Numero}</TableCell>
                                        <TableCell align="center">{r.Cuota}</TableCell>
                                        <TableCell align="right">{fmtMonto(r.DGR_Honorarios)}</TableCell>
                                        <TableCell align="right">{fmtMonto(r.DGR_Gastos)}</TableCell>
                                        <TableCell align="right" sx={sxBsj}>
                                            <Tooltip title={tooltipBsj} arrow placement="top">
                                                <span>{fmtMonto(r.BSJ_Honorarios)}{replicado && ' *'}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right" sx={sxBsj}>
                                            <Tooltip title={tooltipBsj} arrow placement="top">
                                                <span>{fmtMonto(r.BSJ_Gastos)}{replicado && ' *'}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">{fmtMonto(r.RA_Honorarios)}</TableCell>
                                        <TableCell align="right">{fmtMonto(r.RA_Gastos)}</TableCell>
                                        <TableCell>{(r.Origen || '').trim()}</TableCell>
                                        <TableCell><Chip size="small" label={ESTADO_LABEL[r.Estado_Code] || r.Estado_Descripcion || r.Estado_Code} color={ESTADO_COLOR[r.Estado_Code] || 'default'} /></TableCell>
                                        <TableCell align="center">
                                            {r.Id_Plan_DGR ? (
                                                <Tooltip title="Detalle completo del plan">
                                                    <IconButton size="small" onClick={() => setPlanDetalleId(r.Id_Plan_DGR)}>
                                                        <VisibilityIcon fontSize="small" sx={{ color: COLOR }} />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : <span style={{ color: '#bbb' }}>—</span>}
                                        </TableCell>
                                    </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    </>
                    );
                })()}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>
            <DetallePlanDrawer
                open={!!planDetalleId}
                idDetalle={planDetalleId}
                onClose={() => setPlanDetalleId(null)}
            />
        </Dialog>
    );
}

function TablaAuditoriaCertificado({ rows }) {
    const hayReplicados = rows.some(r => Number(r.BSJ_Replicado_En || 0) > 1);
    const [planDetalleId, setPlanDetalleId] = useState(null);
    return (
        <>
        {hayReplicados && (
            <Alert severity="info" icon={false} sx={{ m: 1.5, py: 0.5, fontSize: '0.78rem', borderRadius: `${BR}px`, bgcolor: '#e3f2fd' }}>
                <strong>*</strong> El monto BSJ marcado con asterisco está <strong>repetido en cada certificado</strong> de la misma solicitud/cuota — es <strong>1 solo pago bancario</strong>, no uno por certificado. No sumar entre filas.
            </Alert>
        )}
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0, borderLeft: 0, borderRight: 0 }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ bgcolor: '#fafafa' }}>Sol.</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }}>Serie</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }}>Nº Cert.</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }} align="center">Cuota</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }} align="right">DGR Hon.</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }} align="right">DGR Gas.</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }} align="right">BSJ Hon.</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }} align="right">BSJ Gas.</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }} align="right">RA Hon.</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }} align="right">RA Gas.</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }}>Fecha</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }}>Origen</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }}>Estado</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }}>Proc.</TableCell>
                        <TableCell sx={{ bgcolor: '#fafafa' }} align="center">Detalle</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.length === 0 ? (
                        <TableRow><TableCell colSpan={15} align="center" sx={{ py: 3, color: 'text.secondary' }}>Sin resultados</TableCell></TableRow>
                    ) : rows.map((r, i) => {
                        // BSJ_Replicado_En: cuántos certs comparten el mismo pago BSJ.
                        // Si > 1, el monto BSJ está repetido en cada fila — el pago
                        // realmente es 1 para toda la solicitud, no por certificado.
                        const replicado = Number(r.BSJ_Replicado_En || 0) > 1;
                        const tooltipBsj = replicado
                            ? `Este pago BSJ es de toda la solicitud ($${fmtMonto(r.BSJ_Honorarios)}). Aparece replicado en los ${r.BSJ_Replicado_En} certificados de la misma cuota — NO sumar entre filas.`
                            : '';
                        const sxBsj = replicado
                            ? { color: '#1565c0', fontStyle: 'italic', position: 'relative' }
                            : {};
                        return (
                        <TableRow key={r.Id ? `id-${r.Id}` : `idx-${r.Solicitud}-${r.Serie}-${r.Numero}-${i}`} hover>
                            <TableCell>{r.Solicitud}</TableCell>
                            <TableCell>{r.Serie}</TableCell>
                            <TableCell>{r.Numero}</TableCell>
                            <TableCell align="center">{r.Cuota}</TableCell>
                            <TableCell align="right">{fmtMonto(r.DGR_Honorarios)}</TableCell>
                            <TableCell align="right">{fmtMonto(r.DGR_Gastos)}</TableCell>
                            <TableCell align="right" sx={sxBsj}>
                                <Tooltip title={tooltipBsj} arrow placement="top">
                                    <span>{fmtMonto(r.BSJ_Honorarios)}{replicado && ' *'}</span>
                                </Tooltip>
                            </TableCell>
                            <TableCell align="right" sx={sxBsj}>
                                <Tooltip title={tooltipBsj} arrow placement="top">
                                    <span>{fmtMonto(r.BSJ_Gastos)}{replicado && ' *'}</span>
                                </Tooltip>
                            </TableCell>
                            <TableCell align="right">{fmtMonto(r.RA_Honorarios)}</TableCell>
                            <TableCell align="right">{fmtMonto(r.RA_Gastos)}</TableCell>
                            <TableCell>{fmtFecha(r.Fecha_Deposito)}</TableCell>
                            <TableCell>{r.Origen}{r.SubOrigen ? ' ' + r.SubOrigen : ''}</TableCell>
                            <TableCell><Chip size="small" label={ESTADO_LABEL[r.Estado_Code] || r.Estado_Descripcion || r.Estado_Code} color={ESTADO_COLOR[r.Estado_Code] || 'default'} /></TableCell>
                            <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>{fmtFecha(r.Fecha_Proceso)}<br />{r.Hora_Proceso}</TableCell>
                            <TableCell align="center">
                                {r.Id_Plan_DGR ? (
                                    <Tooltip title="Detalle completo del plan (ciclo end-to-end)">
                                        <IconButton size="small" onClick={() => setPlanDetalleId(r.Id_Plan_DGR)}>
                                            <VisibilityIcon fontSize="small" sx={{ color: COLOR }} />
                                        </IconButton>
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="Sin plan DGR asociado">
                                        <span style={{ color: '#bbb' }}>—</span>
                                    </Tooltip>
                                )}
                            </TableCell>
                        </TableRow>
                    );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
        <DetallePlanDrawer
            open={!!planDetalleId}
            idDetalle={planDetalleId}
            onClose={() => setPlanDetalleId(null)}
        />
        </>
    );
}

// ============================================================================
// Header de drawer reutilizable
// ============================================================================
function DrawerHeader({ icon, titulo, subtitulo, onClose }) {
    // Header prominente: cinta púrpura con el título grande en blanco para
    // que SIEMPRE quede claro qué drawer está abierto (el subtab inferior
    // suele confundir si no hay header visible).
    return (
        <Box sx={{
            p: 2,
            bgcolor: COLOR,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: `4px solid ${COLOR}`,
            position: 'sticky',
            top: 0,
            zIndex: 10,
        }}>
            <Box sx={{ color: 'white', display: 'flex', '& svg': { fontSize: 28 } }}>{icon}</Box>
            <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', lineHeight: 1.2 }}>
                    {titulo}
                </Typography>
                {subtitulo && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>{subtitulo}</Typography>
                )}
            </Box>
            <IconButton onClick={onClose} sx={{ color: 'white' }}>
                <CloseIcon />
            </IconButton>
        </Box>
    );
}

// ============================================================================
// DrawerImportarBSJ
// ============================================================================
function DrawerImportarBSJ({ puedeCargar, puedeEliminar, enqueueSnackbar, onClose, onCambio, modoInicial = 'consultar', plusPagosInicial = false }) {
    const [sub, setSub] = useState(modoInicial);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [filtros, setFiltros] = useState({ idConciliacion: '', fechaDesde: '', fechaHasta: '', plusPagos: '', solicitud: '' });
    const [loading, setLoading] = useState(false);
    const [detalleOpen, setDetalleOpen] = useState(null);

    const [file, setFile] = useState(null);
    const [plusPagos, setPlusPagos] = useState(plusPagosInicial);
    const [importing, setImporting] = useState(false);
    const [ultimoResultado, setUltimoResultado] = useState(null);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await conciliacionBsjListarJWT({ ...filtros, page: page + 1, pageSize });
            if (r?.ok) { setRows(r.items || []); setTotal(r.total || 0); }
        } finally { setLoading(false); }
    }, [filtros, page, pageSize]);

    useEffect(() => { if (sub === 'consultar') cargar(); }, [sub, cargar]);

    const importar = async () => {
        if (!file) return;
        setImporting(true);
        try {
            const r = await conciliacionBsjImportarJWT(file, plusPagos);
            setUltimoResultado(r);
            if (r?.ok) {
                enqueueSnackbar(r.mensaje || 'Importado', { variant: 'success' });
                setFile(null);
                setSub('consultar');
                setPage(0);
                onCambio?.();
            } else {
                enqueueSnackbar(r?.mensaje || 'Error al importar', { variant: 'error' });
            }
        } catch (e) { enqueueSnackbar(String(e), { variant: 'error' }); }
        finally { setImporting(false); }
    };

    const eliminar = async (id) => {
        const r = await conciliacionBsjAnularJWT(id);
        if (r?.ok) {
            enqueueSnackbar(r.mensaje || 'Eliminado', { variant: 'success' });
            cargar();
            onCambio?.();
        } else enqueueSnackbar(r?.mensaje || 'Error', { variant: 'error' });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <DrawerHeader
                icon={<UploadIcon />}
                titulo={sub === 'importar'
                    ? (plusPagos ? 'Importar archivo Botón de Pago' : 'Importar archivo BSJ')
                    : 'BSJ / Botón Pago — Archivos cargados'}
                subtitulo={sub === 'importar'
                    ? (plusPagos
                        ? 'Subir el archivo del canal Botón de Pago'
                        : 'Subir el archivo del Banco San Juan (BSJ)')
                    : 'Consultar los archivos BSJ y Botón de Pago ya importados y su detalle'}
                onClose={onClose}
            />
            <Box sx={{ p: 2, overflowY: 'auto', flexGrow: 1 }}>
                <Tabs value={sub} onChange={(_, v) => setSub(v)} sx={{ mb: 2 }}>
                    <Tab value="consultar" label="Consultar" />
                    <Tab value="importar" label="Importar" disabled={!puedeCargar} />
                </Tabs>

                {sub === 'importar' ? (
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: BR }}>
                        {/* Tabs internos: BSJ vs Botón Pago — visualmente claro qué se está subiendo */}
                        <Tabs value={plusPagos ? 'bp' : 'bsj'} onChange={(_, v) => setPlusPagos(v === 'bp')} sx={{ mb: 2, borderBottom: '1px solid #eee' }}>
                            <Tab value="bsj" label="BSJ (Banco San Juan)" />
                            <Tab value="bp" label="Botón de Pago" />
                        </Tabs>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                            {plusPagos
                                ? 'Archivo del canal Botón de Pago. Mismo formato que BSJ pero se identifica como cobros por canal alternativo (SubOrigen = BP).'
                                : 'Archivo del Banco San Juan, texto plano con posiciones fijas (HEADER, DETAIL, TRAILER). El Id se extrae del HEADER.'}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                            <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
                                {file ? file.name : 'Seleccionar archivo'}
                                <input hidden type="file" accept=".txt,.dat" onChange={e => setFile(e.target.files?.[0] || null)} />
                            </Button>
                            <Button variant="contained" disabled={!file || importing} onClick={importar}
                                sx={{ bgcolor: COLOR }}>
                                {importing ? <CircularProgress size={18} sx={{ color: 'white' }} /> : `Importar ${plusPagos ? 'Botón Pago' : 'BSJ'}`}
                            </Button>
                        </Stack>
                        {ultimoResultado && (
                            <Alert severity={ultimoResultado.ok ? 'success' : 'error'} sx={{ mt: 1 }}>
                                {ultimoResultado.mensaje}
                                {ultimoResultado.id && <> — Id: <strong>{ultimoResultado.id}</strong></>}
                                {ultimoResultado.errores?.length > 0 && (
                                    <Box sx={{ mt: 1, maxHeight: 140, overflow: 'auto' }}>
                                        {ultimoResultado.errores.slice(0, 20).map((e, i) => (<div key={i} style={{ fontSize: 12 }}>• {e}</div>))}
                                        {ultimoResultado.errores.length > 20 && <em>…y {ultimoResultado.errores.length - 20} más</em>}
                                    </Box>
                                )}
                            </Alert>
                        )}
                    </Paper>
                ) : (
                    <>
                        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: BR }}>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                                <TextField size="small" label="Id" value={filtros.idConciliacion}
                                    onChange={e => setFiltros(f => ({ ...f, idConciliacion: e.target.value }))} />
                                <TextField type="date" size="small" label="Desde" InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: '2020-01-01', max: '2099-12-31' }}
                                    value={filtros.fechaDesde} onChange={e => setFiltros(f => ({ ...f, fechaDesde: e.target.value }))} />
                                <TextField type="date" size="small" label="Hasta" InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: '2020-01-01', max: '2099-12-31' }}
                                    value={filtros.fechaHasta} onChange={e => setFiltros(f => ({ ...f, fechaHasta: e.target.value }))} />
                                <TextField select size="small" label="Origen" sx={{ minWidth: 140 }}
                                    value={filtros.plusPagos} onChange={e => setFiltros(f => ({ ...f, plusPagos: e.target.value }))}>
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="0">BSJ</MenuItem>
                                    <MenuItem value="1">Botón Pago</MenuItem>
                                </TextField>
                                <TextField size="small" label="Nº Solicitud" sx={{ width: 140 }}
                                    value={filtros.solicitud}
                                    onChange={e => setFiltros(f => ({ ...f, solicitud: e.target.value }))} />
                                <Button size="small" variant="contained" startIcon={<SearchIcon />}
                                    sx={{ bgcolor: COLOR }} onClick={() => { setPage(0); cargar(); }}>Buscar</Button>
                                <IconButton size="small" onClick={cargar}><RefreshIcon /></IconButton>
                            </Stack>
                        </Paper>

                        {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> : (
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: BR }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: '#fafafa' }}>Id</TableCell>
                                            <TableCell sx={{ bgcolor: '#fafafa' }}>Fecha</TableCell>
                                            <TableCell sx={{ bgcolor: '#fafafa' }}>Usuario</TableCell>
                                            <TableCell sx={{ bgcolor: '#fafafa' }}>Origen</TableCell>
                                            <TableCell sx={{ bgcolor: '#fafafa' }} align="center">Registros</TableCell>
                                            <TableCell sx={{ bgcolor: '#fafafa' }} align="right">Honorarios</TableCell>
                                            <TableCell sx={{ bgcolor: '#fafafa' }} align="right">Acciones</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.length === 0 ? (
                                            <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>Sin resultados</TableCell></TableRow>
                                        ) : rows.map(r => (
                                            <TableRow key={r.Id} hover>
                                                <TableCell sx={{ fontFamily: 'monospace' }}>{r.Id}</TableCell>
                                                <TableCell>{fmtFecha(r.Fecha)}</TableCell>
                                                <TableCell>{r.Usuario}</TableCell>
                                                <TableCell>{r.Plus_Pagos === 1 ? 'Botón Pago' : 'BSJ'}</TableCell>
                                                <TableCell align="center">{r.Total_Detalle}</TableCell>
                                                <TableCell align="right">{fmtMonto(r.Total_Importe)}</TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title="Ver detalle">
                                                        <IconButton size="small" onClick={() => setDetalleOpen(r.Id)}><VisibilityIcon fontSize="small" /></IconButton>
                                                    </Tooltip>
                                                    {puedeEliminar && (
                                                        <Tooltip title="Eliminar">
                                                            <IconButton size="small" color="error" onClick={() => eliminar(r.Id)}><DeleteIcon fontSize="small" /></IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        <TablePagination component="div" count={total} page={page} rowsPerPage={pageSize}
                            onPageChange={(_, p) => setPage(p)}
                            onRowsPerPageChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
                            rowsPerPageOptions={[10, 25, 50, 100]} />

                        <DetalleDialog
                            open={!!detalleOpen} onClose={() => setDetalleOpen(null)} id={detalleOpen}
                            titulo="Detalle BSJ"
                            fetcher={(id, p, ps) => conciliacionBsjDetalleJWT(id, p, ps)}
                            columnas={[
                                { key: 'Solicitud', label: 'Solicitud' },
                                { key: 'Cuota', label: 'Cuota', align: 'center' },
                                { key: 'Importe', label: 'Importe', align: 'right', fmt: fmtMonto },
                                { key: 'Honorarios', label: 'Honorarios', align: 'right', fmt: fmtMonto },
                                { key: 'Gastos', label: 'Gastos', align: 'right', fmt: fmtMonto },
                                { key: 'Tasa', label: 'Tasa', align: 'right', fmt: fmtMonto },
                                { key: 'Fecha', label: 'Fecha', fmt: fmtFecha },
                                { key: 'Estado', label: 'Estado', fmt: (v) => ESTADO_LABEL[(v || '').trim()] || v },
                            ]}
                        />
                    </>
                )}
            </Box>
        </Box>
    );
}

// ============================================================================
// DrawerImportarRA
// ============================================================================
function DrawerImportarRA({ puedeCargar, puedeEliminar, enqueueSnackbar, onClose, onCambio, modoInicial = 'consultar' }) {
    const [sub, setSub] = useState(modoInicial);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [filtros, setFiltros] = useState({ idConciliacion: '', fechaDesde: '', fechaHasta: '', solicitud: '' });
    const [loading, setLoading] = useState(false);
    const [detalleOpen, setDetalleOpen] = useState(null);

    const [file, setFile] = useState(null);
    const [fechaImp, setFechaImp] = useState('');
    const [importing, setImporting] = useState(false);
    const [ultimoResultado, setUltimoResultado] = useState(null);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await conciliacionRaListarJWT({ ...filtros, page: page + 1, pageSize });
            if (r?.ok) { setRows(r.items || []); setTotal(r.total || 0); }
        } finally { setLoading(false); }
    }, [filtros, page, pageSize]);

    useEffect(() => { if (sub === 'consultar') cargar(); }, [sub, cargar]);

    const importar = async () => {
        if (!file || !fechaImp) return;
        setImporting(true);
        try {
            const r = await conciliacionRaImportarJWT(file, fechaImp);
            setUltimoResultado(r);
            if (r?.ok) {
                enqueueSnackbar(r.mensaje, { variant: 'success' });
                setFile(null); setFechaImp(''); setSub('consultar'); setPage(0);
                onCambio?.();
            } else enqueueSnackbar(r?.mensaje || 'Error', { variant: 'error' });
        } catch (e) { enqueueSnackbar(String(e), { variant: 'error' }); }
        finally { setImporting(false); }
    };

    const eliminar = async (id) => {
        const r = await conciliacionRaAnularJWT(id);
        if (r?.ok) {
            enqueueSnackbar(r.mensaje, { variant: 'success' });
            cargar();
            onCambio?.();
        }
        else enqueueSnackbar(r?.mensaje || 'Error', { variant: 'error' });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <DrawerHeader
                icon={<UploadIcon />}
                titulo={sub === 'importar' ? 'Importar archivo Registro Automotor' : 'Registro Automotor — Archivos cargados'}
                subtitulo={sub === 'importar'
                    ? 'Subir un nuevo Excel del Registro Automotor'
                    : 'Consultar los archivos RA ya importados y su detalle'}
                onClose={onClose}
            />
            <Box sx={{ p: 2, overflowY: 'auto', flexGrow: 1 }}>
                <Tabs value={sub} onChange={(_, v) => setSub(v)} sx={{ mb: 2 }}>
                    <Tab value="consultar" label="Consultar" />
                    <Tab value="importar" label="Importar" disabled={!puedeCargar} />
                </Tabs>

                {sub === 'importar' ? (
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: BR }}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Importar Excel de Registro Automotor</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                            Columnas esperadas (sin encabezado): A=Número, B=Serie, C=Solicitud, D=Honorarios, E=Gastos.
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                            <TextField type="date" size="small" label="Fecha" InputLabelProps={{ shrink: true }}
                                inputProps={{ min: '2020-01-01', max: '2099-12-31' }}
                                value={fechaImp} onChange={e => setFechaImp(e.target.value)} />
                            <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
                                {file ? file.name : 'Seleccionar Excel'}
                                <input hidden type="file" accept=".xlsx,.xls" onChange={e => setFile(e.target.files?.[0] || null)} />
                            </Button>
                            <Button variant="contained" disabled={!file || !fechaImp || importing} onClick={importar} sx={{ bgcolor: COLOR }}>
                                {importing ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Importar'}
                            </Button>
                        </Stack>
                        {ultimoResultado && (
                            <Alert severity={ultimoResultado.ok ? 'success' : 'error'}>
                                {ultimoResultado.mensaje}
                                {ultimoResultado.id && <> — Id: <strong>{ultimoResultado.id}</strong></>}
                                {ultimoResultado.errores?.length > 0 && (
                                    <Box sx={{ mt: 1, maxHeight: 140, overflow: 'auto' }}>
                                        {ultimoResultado.errores.slice(0, 20).map((e, i) => (<div key={i} style={{ fontSize: 12 }}>• {e}</div>))}
                                        {ultimoResultado.errores.length > 20 && <em>…y {ultimoResultado.errores.length - 20} más</em>}
                                    </Box>
                                )}
                            </Alert>
                        )}
                    </Paper>
                ) : (
                    <>
                        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: BR }}>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                                <TextField size="small" label="Id" value={filtros.idConciliacion}
                                    onChange={e => setFiltros(f => ({ ...f, idConciliacion: e.target.value }))} />
                                <TextField type="date" size="small" label="Desde" InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: '2020-01-01', max: '2099-12-31' }}
                                    value={filtros.fechaDesde} onChange={e => setFiltros(f => ({ ...f, fechaDesde: e.target.value }))} />
                                <TextField type="date" size="small" label="Hasta" InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: '2020-01-01', max: '2099-12-31' }}
                                    value={filtros.fechaHasta} onChange={e => setFiltros(f => ({ ...f, fechaHasta: e.target.value }))} />
                                <TextField size="small" label="Nº Solicitud" sx={{ width: 140 }}
                                    value={filtros.solicitud}
                                    onChange={e => setFiltros(f => ({ ...f, solicitud: e.target.value }))} />
                                <Button size="small" variant="contained" startIcon={<SearchIcon />}
                                    sx={{ bgcolor: COLOR }} onClick={() => { setPage(0); cargar(); }}>Buscar</Button>
                                <IconButton size="small" onClick={cargar}><RefreshIcon /></IconButton>
                            </Stack>
                        </Paper>

                        {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> : (
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: BR }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: '#fafafa' }}>Id</TableCell>
                                            <TableCell sx={{ bgcolor: '#fafafa' }}>Fecha</TableCell>
                                            <TableCell sx={{ bgcolor: '#fafafa' }}>Usuario</TableCell>
                                            <TableCell sx={{ bgcolor: '#fafafa' }} align="center">Registros</TableCell>
                                            <TableCell sx={{ bgcolor: '#fafafa' }} align="right">Honorarios</TableCell>
                                            <TableCell sx={{ bgcolor: '#fafafa' }} align="right">Acciones</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.length === 0 ? (
                                            <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>Sin resultados</TableCell></TableRow>
                                        ) : rows.map(r => (
                                            <TableRow key={r.Id} hover>
                                                <TableCell sx={{ fontFamily: 'monospace' }}>{r.Id}</TableCell>
                                                <TableCell>{fmtFecha(r.Fecha)}</TableCell>
                                                <TableCell>{r.Usuario}</TableCell>
                                                <TableCell align="center">{r.Total_Detalle}</TableCell>
                                                <TableCell align="right">{fmtMonto(r.Total_Importe)}</TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title="Ver detalle">
                                                        <IconButton size="small" onClick={() => setDetalleOpen(r.Id)}><VisibilityIcon fontSize="small" /></IconButton>
                                                    </Tooltip>
                                                    {puedeEliminar && (
                                                        <Tooltip title="Eliminar">
                                                            <IconButton size="small" color="error" onClick={() => eliminar(r.Id)}><DeleteIcon fontSize="small" /></IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        <TablePagination component="div" count={total} page={page} rowsPerPage={pageSize}
                            onPageChange={(_, p) => setPage(p)}
                            onRowsPerPageChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
                            rowsPerPageOptions={[10, 25, 50, 100]} />

                        <DetalleDialog
                            open={!!detalleOpen} onClose={() => setDetalleOpen(null)} id={detalleOpen}
                            titulo="Detalle RA"
                            fetcher={(id, p, ps) => conciliacionRaDetalleJWT(id, p, ps)}
                            columnas={[
                                { key: 'Solicitud', label: 'Solicitud' },
                                { key: 'Serie', label: 'Serie' },
                                { key: 'Numero', label: 'Número' },
                                { key: 'Honorarios', label: 'Honorarios', align: 'right', fmt: fmtMonto },
                                { key: 'Gastos', label: 'Gastos', align: 'right', fmt: fmtMonto },
                                { key: 'Fecha', label: 'Fecha WSDGR', fmt: fmtFecha },
                                { key: 'Estado', label: 'Estado' },
                                { key: 'Conciliado', label: 'Conc.', align: 'center' },
                            ]}
                        />
                    </>
                )}
            </Box>
        </Box>
    );
}

// ============================================================================
// DrawerConciliar (UNIFICADO: motor + reproceso)
// ============================================================================
// Una sola pantalla con checkboxes para decidir el alcance:
//   - Sin checkboxes → motor rápido (solo cruzamiento sobre lo que ya está)
//   - Re-importar DGR ✓ o Pasaje ✓ → reproceso completo (en background)
function DrawerConciliar({ puedeConciliar, enqueueSnackbar, onClose, onCambio }) {
    const hoy = new Date();
    const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    const [fechaDesde, setFechaDesde] = useState(toISO(hoy));
    const [fechaHasta, setFechaHasta] = useState(toISO(hoy));
    const [reimportarDgr, setReimportarDgr] = useState(false);
    const [pasajeExpediente, setPasajeExpediente] = useState(false);
    const [running, setRunning] = useState(false);
    const [resultado, setResultado] = useState(null);

    const [reprocesos, setReprocesos] = useState([]);
    const [loadingHist, setLoadingHist] = useState(false);
    const [detalle, setDetalle] = useState(null);
    const [detalleOpen, setDetalleOpen] = useState(false);

    const esCompleto = reimportarDgr || pasajeExpediente;

    const cargarHistorial = useCallback(async () => {
        setLoadingHist(true);
        try {
            const r = await conciliacionReprocesoListarJWT(50);
            if (r?.ok) setReprocesos(r.reprocesos || []);
        } finally { setLoadingHist(false); }
    }, []);

    useEffect(() => { cargarHistorial(); }, [cargarHistorial]);

    // Polling cuando hay alguno EN_CURSO
    useEffect(() => {
        const hayEnCurso = reprocesos.some(r => r.Estado === 'EN_CURSO');
        if (!hayEnCurso) return;
        const tid = setInterval(cargarHistorial, 10000);
        return () => clearInterval(tid);
    }, [reprocesos, cargarHistorial]);

    const ejecutar = async () => {
        if (!fechaDesde || !fechaHasta) return;
        setRunning(true); setResultado(null);
        try {
            if (esCompleto) {
                const r = await conciliacionReprocesoIniciarJWT(fechaDesde, fechaHasta);
                if (r?.ok) {
                    enqueueSnackbar(r.mensaje || 'Reproceso iniciado — corre en background', { variant: 'success' });
                    cargarHistorial();
                    onCambio?.();
                } else enqueueSnackbar(r?.mensaje || 'Error', { variant: 'error' });
            } else {
                const r = await conciliacionMotorEjecutarJWT(fechaDesde, fechaHasta);
                setResultado(r);
                if (r?.ok) {
                    enqueueSnackbar(r.mensaje, { variant: 'success' });
                    onCambio?.();
                } else enqueueSnackbar(r?.mensaje || 'Error', { variant: 'error' });
            }
        } catch (e) { enqueueSnackbar(String(e), { variant: 'error' }); }
        finally { setRunning(false); }
    };

    const verDetalle = async (id) => {
        try {
            const r = await conciliacionReprocesoObtenerJWT(id);
            if (r?.ok) { setDetalle(r); setDetalleOpen(true); }
            else enqueueSnackbar(r?.mensaje || 'Error', { variant: 'error' });
        } catch (e) { enqueueSnackbar(String(e), { variant: 'error' }); }
    };

    const chipEstadoRep = (estado) => {
        if (estado === 'EN_CURSO') return <Chip size="small" label="En curso" color="info" icon={<CircularProgress size={12} sx={{ color: 'white !important' }} />} />;
        if (estado === 'COMPLETADO') return <Chip size="small" label="Completado" color="success" />;
        if (estado === 'ERROR') return <Chip size="small" label="Error" color="error" />;
        return <Chip size="small" label={estado || '?'} />;
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <DrawerHeader
                icon={<PlayIcon />}
                titulo="Conciliar"
                subtitulo="Ejecutar el motor tripartita DGR vs BSJ vs RA"
                onClose={onClose}
            />
            <Box sx={{ p: 2, overflowY: 'auto', flexGrow: 1 }}>
                {/* Form ejecución */}
                <Paper variant="outlined" sx={{ p: 2, borderRadius: BR, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: COLOR }}>
                        Ejecutar conciliación
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Para cada fecha del rango: borra el cruce existente, arma el seed DGR, cruza contra BSJ/BP y RA con tolerancia de ±$10 en honorarios y gastos.
                        Rango máximo 3 meses.
                    </Typography>

                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                        <TextField type="date" size="small" label="Desde" InputLabelProps={{ shrink: true }}
                            inputProps={{ min: '2020-01-01', max: '2099-12-31' }}
                            value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
                        <TextField type="date" size="small" label="Hasta" InputLabelProps={{ shrink: true }}
                            inputProps={{ min: '2020-01-01', max: '2099-12-31' }}
                            value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
                    </Stack>

                    {/* Opciones avanzadas */}
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: BR, bgcolor: '#fafbfc' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', display: 'block', mb: 1 }}>
                            OPCIONES AVANZADAS
                        </Typography>
                        <Stack spacing={0.5}>
                            <FormControlLabel control={
                                <Checkbox size="small" checked={reimportarDgr} onChange={e => setReimportarDgr(e.target.checked)} />
                            } label={
                                <Box>
                                    <Typography variant="body2">Re-importar planes DGR del WS-Rentas primero</Typography>
                                    <Typography variant="caption" color="text.secondary">El cron diario ya lo hace cada 6:00 AM. Marcalo si necesitás forzarlo ahora (datos nuevos del WS).</Typography>
                                </Box>
                            } />
                            <FormControlLabel control={
                                <Checkbox size="small" checked={pasajeExpediente} onChange={e => setPasajeExpediente(e.target.checked)} />
                            } label={
                                <Box>
                                    <Typography variant="body2">Forzar pasaje a expediente sobre este rango</Typography>
                                    <Typography variant="caption" color="text.secondary">El cron diario ya pasa los conciliados a expediente automáticamente. Marcalo solo si querés re-procesar este rango específico ahora.</Typography>
                                </Box>
                            } />
                        </Stack>
                    </Paper>

                    {esCompleto && (
                        <Alert severity="info" sx={{ mt: 1.5 }}>
                            Con estas opciones se ejecuta el <strong>reproceso completo</strong> en segundo plano. Vas a ver el progreso en el historial abajo.
                            Normalmente no hace falta — el cron diario hace lo mismo todas las noches.
                        </Alert>
                    )}

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" startIcon={running ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <PlayIcon />}
                            disabled={!fechaDesde || !fechaHasta || running || !puedeConciliar}
                            onClick={ejecutar}
                            sx={{ bgcolor: COLOR, '&:hover': { bgcolor: '#4a1370' }, textTransform: 'none', fontWeight: 600 }}>
                            {running ? (esCompleto ? 'Iniciando reproceso…' : 'Conciliando…') : (esCompleto ? 'Iniciar reproceso completo' : 'Conciliar (motor rápido)')}
                        </Button>
                    </Box>
                </Paper>

                {/* Resultado motor rápido */}
                {resultado?.ok && resultado.dias && (
                    <Paper variant="outlined" sx={{ borderRadius: BR, mb: 2 }}>
                        <Box sx={{ p: 1.5, bgcolor: '#fafafa', borderBottom: '1px solid #e0e6ef' }}>
                            <Typography variant="subtitle2">Resultado ({resultado.dias.length} día(s), {resultado.totalInsertados} filas sembradas)</Typography>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ bgcolor: '#fafafa' }}>Fecha</TableCell>
                                        <TableCell sx={{ bgcolor: '#fafafa' }} align="center">Borrados</TableCell>
                                        <TableCell sx={{ bgcolor: '#fafafa' }} align="center">DGR seed</TableCell>
                                        <TableCell sx={{ bgcolor: '#fafafa' }} align="center">BSJ proc.</TableCell>
                                        <TableCell sx={{ bgcolor: '#fafafa' }} align="center">BSJ V</TableCell>
                                        <TableCell sx={{ bgcolor: '#fafafa' }} align="center">BSJ X</TableCell>
                                        <TableCell sx={{ bgcolor: '#fafafa' }} align="center">BSJ W</TableCell>
                                        <TableCell sx={{ bgcolor: '#fafafa' }} align="center">RA proc.</TableCell>
                                        <TableCell sx={{ bgcolor: '#fafafa' }} align="center">RA V</TableCell>
                                        <TableCell sx={{ bgcolor: '#fafafa' }} align="center">RA X</TableCell>
                                        <TableCell sx={{ bgcolor: '#fafafa' }} align="center">RA huérf.</TableCell>
                                        <TableCell sx={{ bgcolor: '#fafafa' }} align="center">Dup.</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {resultado.dias.map(d => (
                                        <TableRow key={d.fecha} hover>
                                            <TableCell>{fmtFecha(d.fecha)}</TableCell>
                                            <TableCell align="center">{d.borrados}</TableCell>
                                            <TableCell align="center">{d.seedDGR}</TableCell>
                                            <TableCell align="center">{d.bsjProcesados}</TableCell>
                                            <TableCell align="center" sx={{ color: 'success.main' }}>{d.bsjConciliados}</TableCell>
                                            <TableCell align="center" sx={{ color: 'error.main' }}>{d.bsjSinCoincidencia}</TableCell>
                                            <TableCell align="center" sx={{ color: 'info.main' }}>{d.bsjSinSolicitud}</TableCell>
                                            <TableCell align="center">{d.raProcesados}</TableCell>
                                            <TableCell align="center" sx={{ color: 'success.main' }}>{d.raConciliados}</TableCell>
                                            <TableCell align="center" sx={{ color: 'error.main' }}>{d.raSinCoincidencia}</TableCell>
                                            <TableCell align="center">{d.raHuerfanos}</TableCell>
                                            <TableCell align="center">{d.duplicadosBSJ + d.duplicadosRA}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}

                {/* Historial de reprocesos */}
                <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#444', flexGrow: 1 }}>
                        Historial de reprocesos completos
                    </Typography>
                    <IconButton size="small" onClick={cargarHistorial}><RefreshIcon /></IconButton>
                </Stack>

                {loadingHist && <Box sx={{ textAlign: 'center', p: 2 }}><CircularProgress size={24} /></Box>}

                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: BR }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ bgcolor: '#fafafa' }}>Inicio</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }}>Período</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }}>Origen</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }}>Usuario</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }}>Estado</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }} align="right">Novedades</TableCell>
                                <TableCell sx={{ bgcolor: '#fafafa' }}>Fin</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {reprocesos.length === 0 && !loadingHist && (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ color: '#999', py: 3 }}>
                                    Sin reprocesos aún. El motor rápido se usa sin generar historial.
                                </TableCell></TableRow>
                            )}
                            {reprocesos.map(r => {
                                const totalNov = r.Resumen?.novedadesPorEstado
                                    ? Object.values(r.Resumen.novedadesPorEstado).reduce((a, b) => Number(a) + Number(b), 0)
                                    : 0;
                                return (
                                    <TableRow key={r.Id} hover onClick={() => verDetalle(r.Id)} sx={{ cursor: 'pointer' }}>
                                        <TableCell>{fmtDateTime(r.Inicio)}</TableCell>
                                        <TableCell>{fmtFecha(r.FechaDesde)} – {fmtFecha(r.FechaHasta)}</TableCell>
                                        <TableCell>{r.Origen}</TableCell>
                                        <TableCell>{r.Usuario}</TableCell>
                                        <TableCell>{chipEstadoRep(r.Estado)}</TableCell>
                                        <TableCell align="right">{r.Estado === 'COMPLETADO' ? fmt(totalNov) : '-'}</TableCell>
                                        <TableCell>{fmtDateTime(r.Fin)}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Dialog detalle de reproceso */}
            <Dialog open={detalleOpen} onClose={() => setDetalleOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Detalle del reproceso #{detalle?.reproceso?.Id}</DialogTitle>
                <DialogContent dividers>
                    {detalle && (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <b>Período:</b> {fmtFecha(detalle.reproceso.FechaDesde)} – {fmtFecha(detalle.reproceso.FechaHasta)} · <b>Origen:</b> {detalle.reproceso.Origen} · <b>Estado:</b> {detalle.reproceso.Estado}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666' }}>
                                Inicio: {fmtDateTime(detalle.reproceso.Inicio)} · Fin: {fmtDateTime(detalle.reproceso.Fin) || 'en curso'}
                            </Typography>

                            {detalle.reproceso.ErrorMsg && (
                                <Alert severity="error" sx={{ my: 1.5 }}>{detalle.reproceso.ErrorMsg}</Alert>
                            )}

                            {detalle.reproceso.Resumen && (
                                <Paper variant="outlined" sx={{ p: 1.5, my: 1.5, borderRadius: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Resumen</Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {Object.entries(detalle.reproceso.Resumen.novedadesPorEstado || {}).map(([k, v]) => (
                                            <Chip key={k} size="small" label={`${ESTADO_LABEL[k] || k}: ${v}`} sx={{ bgcolor: (ESTADO_HEX[k] || '#999') + '22' }} />
                                        ))}
                                        <Chip size="small" label={`Vinculados: ${detalle.reproceso.Resumen.pasajeAExpedienteVinculados || 0}`} color="success" variant="outlined" />
                                        <Chip size="small" label={`Pend. expediente: ${detalle.reproceso.Resumen.pasajeAExpedientePendientes || 0}`} color="warning" variant="outlined" />
                                        <Chip size="small" label={`Import: ${detalle.reproceso.Resumen.segundosImport}s`} variant="outlined" />
                                        <Chip size="small" label={`Conciliación: ${detalle.reproceso.Resumen.segundosConciliacion}s`} variant="outlined" />
                                        <Chip size="small" label={`Pasaje: ${detalle.reproceso.Resumen.segundosPasaje}s`} variant="outlined" />
                                    </Stack>
                                </Paper>
                            )}

                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5 }}>
                                Novedades ({detalle.novedades.length})
                            </Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, borderRadius: 1 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ bgcolor: '#fafafa' }}>Solicitud</TableCell>
                                            <TableCell sx={{ bgcolor: '#fafafa' }} align="center">Cuota</TableCell>
                                            <TableCell sx={{ bgcolor: '#fafafa' }}>Fecha pago</TableCell>
                                            <TableCell sx={{ bgcolor: '#fafafa' }}>Antes</TableCell>
                                            <TableCell sx={{ bgcolor: '#fafafa' }}>Ahora</TableCell>
                                            <TableCell sx={{ bgcolor: '#fafafa' }} align="center">Pasado a expediente</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {detalle.novedades.length === 0 && (
                                            <TableRow><TableCell colSpan={6} align="center" sx={{ color: '#999', py: 2 }}>Sin novedades — no cambió ningún estado.</TableCell></TableRow>
                                        )}
                                        {detalle.novedades.map(n => (
                                            <TableRow key={n.Id} hover>
                                                <TableCell>{n.Solicitud}</TableCell>
                                                <TableCell align="center">{n.Cuota}</TableCell>
                                                <TableCell>{fmtFecha(n.Fecha_Deposito)}</TableCell>
                                                <TableCell>{n.Estado_Anterior ? <Chip size="small" label={ESTADO_LABEL[n.Estado_Anterior] || n.Estado_Anterior} sx={{ bgcolor: (ESTADO_HEX[n.Estado_Anterior] || '#999') + '22' }} /> : <em style={{ color: '#999' }}>(nuevo)</em>}</TableCell>
                                                <TableCell><Chip size="small" label={ESTADO_LABEL[n.Estado_Nuevo] || n.Estado_Nuevo} sx={{ bgcolor: (ESTADO_HEX[n.Estado_Nuevo] || '#999') + '22' }} /></TableCell>
                                                <TableCell align="center">{n.Paso_A_Auditoria ? <Chip size="small" label="Sí" color="success" /> : '-'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetalleOpen(false)}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

// ============================================================================
// DrawerDashboard
// ============================================================================
function DrawerDashboard({ onClose }) {
    const hoy = new Date();
    const primeroMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    const [fechaDesde, setFechaDesde] = useState(toISO(primeroMes));
    const [fechaHasta, setFechaHasta] = useState(toISO(hoy));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await conciliacionDashboardJWT(fechaDesde, fechaHasta);
            if (r?.ok) setData(r.dashboard);
            else setData(null);
        } finally { setLoading(false); }
    }, [fechaDesde, fechaHasta]);

    useEffect(() => { cargar(); }, [cargar]);

    const porEstado = data?.porEstado || [];
    const evolucion = data?.evolucion || [];
    const comp = data?.comparativo || {};

    const totalRegistros = porEstado.reduce((a, e) => a + (e.Cantidad || 0), 0);
    const montoTotal = (e) => (e.Monto_DGR || 0) + (e.Monto_BSJ || 0) + (e.Monto_RA || 0);

    const maxEvol = Math.max(1, ...evolucion.map(d => (d.Conciliados || 0) + (d.Pendientes || 0)));

    const totDGR = (comp.DGR_Honorarios || 0) + (comp.DGR_Gastos || 0);
    const totBSJ = (comp.BSJ_Honorarios || 0) + (comp.BSJ_Gastos || 0);
    const totRA = (comp.RA_Honorarios || 0) + (comp.RA_Gastos || 0);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <DrawerHeader
                icon={<DashboardIcon />}
                titulo="Dashboard"
                subtitulo="Resumen contable: estado, evolución y comparativo por origen"
                onClose={onClose}
            />
            <Box sx={{ p: 2, overflowY: 'auto', flexGrow: 1 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                    <TextField type="date" size="small" label="Desde" InputLabelProps={{ shrink: true }}
                        inputProps={{ min: '2020-01-01', max: '2099-12-31' }}
                        value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
                    <TextField type="date" size="small" label="Hasta" InputLabelProps={{ shrink: true }}
                        inputProps={{ min: '2020-01-01', max: '2099-12-31' }}
                        value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
                    <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={cargar}
                        sx={{ color: COLOR, borderColor: COLOR }}>
                        Actualizar
                    </Button>
                    {loading && <CircularProgress size={20} />}
                </Stack>

                {/* Por estado */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#444', mb: 1 }}>Por estado</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 1.5, mb: 3 }}>
                    {['V', 'X', 'S', 'W', 'D'].map(code => {
                        const e = porEstado.find(x => (x.Estado_Code || '').trim() === code) || { Cantidad: 0 };
                        const pct = totalRegistros > 0 ? Math.round((e.Cantidad || 0) / totalRegistros * 100) : 0;
                        return (
                            <Paper key={code} variant="outlined" sx={{ p: 1.5, borderRadius: BR, borderLeft: `4px solid ${ESTADO_HEX[code]}` }}>
                                <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>{ESTADO_LABEL[code]}</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: ESTADO_HEX[code], lineHeight: 1.2 }}>{fmt(e.Cantidad)}</Typography>
                                <Typography variant="caption" sx={{ color: '#999' }}>{pct}% · ${fmt(montoTotal(e))}</Typography>
                            </Paper>
                        );
                    })}
                </Box>

                {/* Evolución */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#444', mb: 1 }}>Evolución por día</Typography>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: BR, mb: 3, overflowX: 'auto' }}>
                    {evolucion.length === 0 ? (
                        <Typography variant="body2" sx={{ color: '#999' }}>Sin datos en el rango.</Typography>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, minHeight: 160, minWidth: evolucion.length * 28 }}>
                            {evolucion.map((d, i) => {
                                const tot = (d.Conciliados || 0) + (d.Pendientes || 0);
                                const hConc = tot > 0 ? (d.Conciliados / maxEvol) * 140 : 0;
                                const hPend = tot > 0 ? (d.Pendientes / maxEvol) * 140 : 0;
                                return (
                                    <Tooltip key={i} title={`${fmtFecha(d.Fecha)} · Conciliados: ${d.Conciliados} · Pendientes: ${d.Pendientes}`}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', width: 24 }}>
                                            <Box sx={{ width: 18, height: hPend, bgcolor: '#e65100', borderRadius: '2px 2px 0 0' }} />
                                            <Box sx={{ width: 18, height: hConc, bgcolor: '#2e7d32', borderRadius: hPend > 0 ? 0 : '2px 2px 0 0' }} />
                                            <Typography variant="caption" sx={{ fontSize: 9, color: '#999', mt: 0.3, transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>
                                                {String(d.Fecha).slice(8, 10)}/{String(d.Fecha).slice(5, 7)}
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Box>
                    )}
                    <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center"><Box sx={{ width: 12, height: 12, bgcolor: '#2e7d32', borderRadius: '2px' }} /><Typography variant="caption">Conciliados</Typography></Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center"><Box sx={{ width: 12, height: 12, bgcolor: '#e65100', borderRadius: '2px' }} /><Typography variant="caption">Pendientes</Typography></Stack>
                    </Stack>
                </Paper>

                {/* Comparativo por origen */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#444', mb: 1 }}>Comparativo por origen (honorarios + gastos)</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                    {[
                        { label: 'DGR', monto: totDGR, hon: comp.DGR_Honorarios, gas: comp.DGR_Gastos, color: '#6a1b9a' },
                        { label: 'Banco San Juan (BSJ)', monto: totBSJ, hon: comp.BSJ_Honorarios, gas: comp.BSJ_Gastos, color: '#1565c0' },
                        { label: 'Registro Automotor (RA)', monto: totRA, hon: comp.RA_Honorarios, gas: comp.RA_Gastos, color: '#2e7d6f' },
                    ].map(o => (
                        <Paper key={o.label} variant="outlined" sx={{ p: 2, borderRadius: BR, borderTop: `3px solid ${o.color}` }}>
                            <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>{o.label}</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: o.color }}>${fmt(o.monto)}</Typography>
                            <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>Honorarios: ${fmt(o.hon)}</Typography>
                            <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>Gastos: ${fmt(o.gas)}</Typography>
                        </Paper>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

// ============================================================================
// DetalleDialog genérico (reutilizado por BSJ y RA)
// ============================================================================
function DetalleDialog({ open, onClose, id, titulo, fetcher, columnas }) {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !id) return;
        setLoading(true);
        fetcher(id, page + 1, pageSize)
            .then(r => { if (r?.ok) { setRows(r.items || []); setTotal(r.total || 0); } })
            .finally(() => setLoading(false));
    }, [open, id, page, pageSize, fetcher]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: BR } }}>
            <DialogTitle sx={{ bgcolor: COLOR, color: 'white' }}>{titulo} — {id}</DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
                {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : (
                    <TableContainer>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {columnas.map(c => (
                                        <TableCell key={c.key} sx={{ bgcolor: '#fafafa' }} align={c.align || 'left'}>{c.label}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 ? (
                                    <TableRow><TableCell colSpan={columnas.length} align="center" sx={{ py: 3, color: 'text.secondary' }}>Sin datos</TableCell></TableRow>
                                ) : rows.map((r, i) => (
                                    <TableRow key={r.Id || i} hover>
                                        {columnas.map(c => (
                                            <TableCell key={c.key} align={c.align || 'left'}>
                                                {c.fmt ? c.fmt(r[c.key]) : r[c.key]}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <TablePagination component="div" count={total} page={page} rowsPerPage={pageSize}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
                    rowsPerPageOptions={[25, 50, 100, 250]} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
}
